import MarketSelect from '@/components/base/MarketSelect';
import FilterSelect from '@/components/base/FilterSelect';

const statusOptions = [
  { id: 'all', label: 'All', icon: 'ri-stack-line' },
  { id: 'Active', label: 'Active', icon: 'ri-play-circle-line' },
  { id: 'Recruiting', label: 'Recruiting', icon: 'ri-user-add-line' },
  { id: 'Review', label: 'Review', icon: 'ri-eye-line' },
  { id: 'Paying', label: 'Paying', icon: 'ri-wallet-3-line' },
  { id: 'Draft', label: 'Draft', icon: 'ri-draft-line' },
  { id: 'Completed', label: 'Completed', icon: 'ri-check-double-line' },
  { id: 'Archived', label: 'Archived', icon: 'ri-archive-line' },
];

interface CampaignFilterBarProps {
  status: string;
  markets: string[];
  query: string;
  counts: Record<string, number>;
  activeOnly: boolean;
  activeCount: number;
  attentionOnly: boolean;
  attentionCount: number;
  onStatus: (value: string) => void;
  onSetMarkets: (value: string[]) => void;
  onQuery: (value: string) => void;
  onToggleActive: () => void;
  onToggleAttention: () => void;
}

export default function CampaignFilterBar({
  status,
  markets,
  query,
  counts,
  activeOnly,
  activeCount,
  attentionOnly,
  attentionCount,
  onStatus,
  onSetMarkets,
  onQuery,
  onToggleActive,
  onToggleAttention,
}: CampaignFilterBarProps) {
  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <FilterSelect
        value={status}
        onChange={onStatus}
        label="Status"
        className="w-full sm:w-56"
        options={statusOptions.map((option) => ({
          ...option,
          count: option.id === 'all' ? counts.all : counts[option.id] ?? 0,
        }))}
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <button
          type="button"
          aria-pressed={activeOnly}
          onClick={onToggleActive}
          className={`flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-sm font-label font-medium transition-colors ${
            activeOnly
              ? 'border-primary-500 bg-primary-500 text-background-50'
              : 'border-primary-200 bg-primary-50 text-primary-800 hover:border-primary-400'
          }`}
        >
          <i className="ri-play-circle-line" />
          Active &amp; recruiting
          <span
            className={`rounded-full px-1.5 text-[11px] font-semibold ${
              activeOnly
                ? 'bg-background-50/25 text-background-50'
                : 'bg-background-50 text-primary-800'
            }`}
          >
            {activeCount}
          </span>
        </button>

        <button
          type="button"
          aria-pressed={attentionOnly}
          onClick={onToggleAttention}
          className={`flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 text-sm font-label font-medium transition-colors ${
            attentionOnly
              ? 'border-secondary-500 bg-secondary-500 text-background-50'
              : 'border-secondary-200 bg-secondary-100 text-secondary-900 hover:border-secondary-400'
          }`}
        >
          <i className="ri-flag-line" />
          Need attention
          <span
            className={`rounded-full px-1.5 text-[11px] font-semibold ${
              attentionOnly
                ? 'bg-background-50/25 text-background-50'
                : 'bg-background-50 text-secondary-900'
            }`}
          >
            {attentionCount}
          </span>
        </button>

        <MarketSelect
          value={markets}
          onChange={onSetMarkets}
          placeholder="All markets"
          compact
          className="sm:w-56"
        />

        <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
          <i className="ri-search-line text-foreground-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search campaigns or brands..."
            className="h-9 w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none sm:w-56"
          />
        </div>
      </div>
    </div>
  );
}